/**
 * Button atoms — Primary / Secondary / Ghost.
 *
 * Per the design-folder `build-notes.md`:
 *   - PrimaryButton  → dark-fill near-black (`--primary`) + white text
 *   - SecondaryButton → white bg + dark outline (`--border-strong`)
 *   - GhostButton    → transparent, hover lifts to `--surface-muted`
 *
 * All three render at 36px height with 8px radius (`--r-sm`) and
 * 13px/600 typography — sized per the projects-page.css `.btn` rule.
 *
 * Implementation note: Mantine v7's polymorphic typing (e.g.
 * `component={Link}`) fights TypeScript inference when wrapped. We
 * keep the wrapper's prop surface strictly typed and cast the
 * Mantine `Button` to `any` at the single JSX site so consumers
 * still get full IntelliSense.
 */
import { Button as MantineButton } from '@mantine/core';
import { forwardRef, type ReactNode } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MB = MantineButton as any;

type BaseProps = {
  children: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  disabled?: boolean;
  loading?: boolean;
  /** Render as another element/component (e.g. `Link` from react-router). */
  component?: React.ElementType;
  to?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  rel?: string;
  target?: string;
};

const baseRootStyle = {
  height: 36,
  paddingInline: 14,
  fontSize: 13,
  fontWeight: 600,
};

export const PrimaryButton = forwardRef<HTMLElement, BaseProps>(
  function PrimaryButton({ children, leftIcon, rightIcon, ...rest }, ref) {
    return (
      <MB
        ref={ref}
        variant="filled"
        color="blue"
        radius="sm"
        size="sm"
        styles={{
          root: {
            ...baseRootStyle,
            background: 'var(--primary)',
            borderColor: 'var(--primary)',
            color: '#fff',
          },
        }}
        leftSection={leftIcon}
        rightSection={rightIcon}
        {...rest}
      >
        {children}
      </MB>
    );
  },
);

export const SecondaryButton = forwardRef<HTMLElement, BaseProps>(
  function SecondaryButton({ children, leftIcon, rightIcon, ...rest }, ref) {
    return (
      <MB
        ref={ref}
        variant="default"
        radius="sm"
        size="sm"
        styles={{
          root: {
            ...baseRootStyle,
            background: 'var(--surface)',
            color: 'var(--text)',
            borderColor: 'var(--border-strong)',
          },
        }}
        leftSection={leftIcon}
        rightSection={rightIcon}
        {...rest}
      >
        {children}
      </MB>
    );
  },
);

export const GhostButton = forwardRef<
  HTMLElement,
  BaseProps & { tone?: 'default' | 'danger' }
>(function GhostButton({ children, leftIcon, rightIcon, tone = 'default', ...rest }, ref) {
  return (
    <MB
      ref={ref}
      variant="subtle"
      radius="sm"
      size="sm"
      styles={{
        root: {
          ...baseRootStyle,
          paddingInline: 12,
          color: tone === 'danger' ? 'var(--failed-fg)' : 'var(--text-muted)',
          background: 'transparent',
        },
      }}
      leftSection={leftIcon}
      rightSection={rightIcon}
      {...rest}
    >
      {children}
    </MB>
  );
});
